import { SKILL_TIERS, getSkillTierIndex } from '../../skillTiers'
import type { CalibrationState } from '../../calibration'
import type { SeasonStats } from '../../types'
import './TierMark.css'

/**
 * A player's Intuition Rating reduced to its glyph and its tier name — the identity-sized version
 * of the profile's rating card, for surfaces that are about the person rather than the stat.
 *
 * No percentage, no bar, no next-tier line: those belong to `SkillMeter`, which is the place the
 * rating is the subject. Here it sits under a player's name, so it has to read as a rank.
 *
 * Renders nothing at all while the player is calibrating. Their first three scored gameweeks
 * swing the tier wildly, which is the whole reason `calibration.ts` exists; a "Calibrating" label
 * would be a placeholder in a slot that is better empty.
 */
export default function TierMark({
  seasonStats,
  calibration,
  className,
}: {
  seasonStats: SeasonStats
  calibration: CalibrationState
  className?: string
}) {
  if (calibration.isCalibrating) return null

  const tier = SKILL_TIERS[getSkillTierIndex(seasonStats.correctOutcomePct ?? 0)]
  const TierIcon = tier.icon

  return (
    <span
      className={`tier-mark ${className ?? ''}`}
      style={{ '--tier-text-color': tier.textColor } as React.CSSProperties}
    >
      <TierIcon className="tier-mark__icon" aria-hidden="true" />
      {/* Deliberately NOT --font-display, unlike SkillMeter's tier name: there the tier name and
          the player's name are never adjacent, here they are stacked, and sharing Montserrat 800
          reads as one doubled headline. */}
      <span className="tier-mark__name label-eyebrow">{tier.name}</span>
    </span>
  )
}
